/**
 * Cart Synchronization Utility for Shopify Liquid Store
 * Shares the cart token with the other Redmond storefronts (Hydrogen-based)
 * through a cookie on the shared parent domain. All storefronts sell from
 * the same Shopify shop, so a Hydrogen cart ID can be adopted here — but
 * only after verifying Shopify actually loads it; the local cart is never
 * destroyed and the page is never force-reloaded.
 */

(function () {
  'use strict';

  const CONFIG = {
    CART_COOKIE_NAME: 'cart',
    SYNC_COOKIE_NAME: 'redmond_cart_sync',
    SHARED_DOMAIN: '.redmond.com',
    COOKIE_MAX_AGE: 60 * 60 * 24 * 30,
    GID_PREFIX: 'gid://shopify/Cart/',
    CURRENT_STORE_URL: (window.CART_SYNC_CONFIG && window.CART_SYNC_CONFIG.CURRENT_STORE_URL) || window.location.origin,
    STORE_URLS: (window.CART_SYNC_CONFIG && window.CART_SYNC_CONFIG.STORE_URLS) || {}
  };

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length >= 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  function setSharedCookie(value) {
    document.cookie =
      `${CONFIG.SYNC_COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; Domain=${CONFIG.SHARED_DOMAIN}; SameSite=Lax; Max-Age=${CONFIG.COOKIE_MAX_AGE}; Secure`;
  }

  function setHostCookie(value) {
    document.cookie =
      `${CONFIG.CART_COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; SameSite=Lax; Max-Age=${CONFIG.COOKIE_MAX_AGE}; Secure`;
  }

  function clearLegacySharedCookie() {
    document.cookie =
      `${CONFIG.CART_COOKIE_NAME}=; Path=/; Domain=${CONFIG.SHARED_DOMAIN}; SameSite=Lax; Max-Age=0; Secure`;
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function normalizeToken(value) {
    if (!value || typeof value !== 'string') return '';
    let token = safeDecode(value);
    if (token.startsWith(CONFIG.GID_PREFIX)) {
      token = token.slice(CONFIG.GID_PREFIX.length);
    }
    return token;
  }

  function baseToken(value) {
    return normalizeToken(value).split('?')[0];
  }

  function getStoreBaseUrl(identity) {
    const normalizedIdentity = (identity || '').toString().toLowerCase();

    if (
      normalizedIdentity.includes('equine') ||
      normalizedIdentity.includes('horse') ||
      normalizedIdentity.includes('daily gold')
    ) {
      return CONFIG.STORE_URLS.equine || 'https://equine.redmond.com';
    }

    if (normalizedIdentity.includes('agriculture') || normalizedIdentity.includes('ag ')) {
      return CONFIG.STORE_URLS.agriculture || 'https://agriculture.redmond.com';
    }

    if (normalizedIdentity.includes('hunt') || normalizedIdentity.includes('trophy')) {
      return CONFIG.STORE_URLS.hunt || 'https://hunt.redmond.com';
    }

    return CONFIG.STORE_URLS.living || 'https://living.redmond.com';
  }

  function getCanonicalProductUrl(item) {
    if (!item) return '';

    const identity = [
      item.vendor,
      item.product_type,
      item.product_title,
      item.title,
      item.handle
    ]
      .filter(Boolean)
      .join(' ');
    const baseUrl = getStoreBaseUrl(identity);
    let path = item.url || '';

    if (!path && item.handle) {
      path = '/products/' + item.handle;
    }

    if (!path) return '';

    try {
      path = new URL(path, window.location.origin).pathname;
    } catch (error) {
      path = String(path).split('?')[0].split('#')[0];
    }

    try {
      return new URL(path, baseUrl).toString();
    } catch (error) {
      return String(baseUrl).replace(/\/$/, '') + path;
    }
  }

  /**
   * Hydrogen stores (hunt, equine, agriculture) use cart IDs with a "?key="
   * suffix (often URL-encoded as "%3Fkey%3D"); classic Liquid tokens may not
   * have it.
   */
  function isHydrogenCartId(cartId) {
    return Boolean(cartId && (cartId.includes('?key=') || cartId.includes('%3Fkey%3D')));
  }

  async function fetchLocalCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        cache: 'no-store',
        headers: {'Content-Type': 'application/json'}
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('[Cart Sync] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Publish the local cart token to the shared parent-domain cookie. Always
   * overwrites: a stale foreign token left in the domain cookie shadows the
   * host cookie and makes every /cart.js read come back empty.
   */
  function broadcastToken(token) {
    const normalized = normalizeToken(token);
    if (!normalized) return;
    if (baseToken(getCookie(CONFIG.CART_COOKIE_NAME)) === baseToken(normalized)) return;
    setSharedCookie(normalized);
  }

  /**
   * On page load, reconcile the shared cookie with the local Shopify cart.
   * - No shared cookie: publish the local token.
   * - Tokens match: nothing to do.
   * - Foreign token + empty local cart: try to adopt it (same shop), but
   *   verify /cart.js actually resolves it before trusting it.
   * - Otherwise the local cart wins and the shared cookie is overwritten,
   *   which also self-heals browsers left in a poisoned state.
   */
  async function reconcileSharedCart() {
    clearLegacySharedCookie();

    const localCart = await fetchLocalCart();
    if (!localCart) return;
    const localToken = localCart.token || '';
    const sharedRaw = getCookie(CONFIG.SYNC_COOKIE_NAME);

    if (!sharedRaw) {
      if (localToken) setSharedCookie(normalizeToken(localToken));
      return;
    }

    const sharedBase = baseToken(sharedRaw);
    if (!sharedBase || sharedBase === baseToken(localToken)) return;

    if (localCart.item_count === 0) {
      setHostCookie(normalizeToken(sharedRaw));
      const adoptedCart = await fetchLocalCart();
      if (adoptedCart && baseToken(adoptedCart.token) === sharedBase) {
        if (adoptedCart.item_count > 0 && typeof window.updateCartBadge === 'function') {
          window.updateCartBadge(adoptedCart.item_count);
        }
        return;
      }
      // Adoption failed; keep the shared token intact so another storefront
      // does not get poisoned by a fresh empty cart token.
      return;
    }

    setSharedCookie(normalizeToken(localToken));
  }

  function interceptCartOperations() {
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const url = args[0];

      if (typeof url === 'string' && (
        url.includes('/cart/add') ||
        url.includes('/cart/update') ||
        url.includes('/cart/change') ||
        url.includes('/cart/clear')
      )) {
        return originalFetch.apply(this, args).then(async (response) => {
          const clonedResponse = response.clone();
          try {
            const data = await clonedResponse.json();
            if (data.token) {
              broadcastToken(data.token);
            } else {
              const localCart = await fetchLocalCart();
              if (localCart && localCart.token) broadcastToken(localCart.token);
            }
          } catch (error) {
            /* not JSON */
          }
          return response;
        });
      }

      return originalFetch.apply(this, args);
    };

    if (window.jQuery) {
      const originalAjax = jQuery.ajax;
      jQuery.ajax = function (url, options) {
        if (typeof url === 'object') {
          options = url;
          url = options.url;
        }

        if (typeof url === 'string' && (
          url.includes('/cart/add') ||
          url.includes('/cart/update') ||
          url.includes('/cart/change') ||
          url.includes('/cart/clear')
        )) {
          const originalSuccess = options && options.success;
          if (options) {
            options.success = function (data, textStatus, jqXHR) {
              if (data && data.token) {
                broadcastToken(data.token);
              }
              if (originalSuccess) {
                originalSuccess.apply(this, arguments);
              }
            };
          }
        }

        return originalAjax.call(this, url, options);
      };
    }
  }

  function interceptFormSubmissions() {
    document.addEventListener('submit', function (e) {
      const form = e.target;
      const action = form.action || '';

      if (action.includes('/cart/add') || action.includes('/cart/update') || action.includes('/cart')) {
        setTimeout(async () => {
          const localCart = await fetchLocalCart();
          if (localCart && localCart.token) {
            broadcastToken(localCart.token);
          }
        }, 500);
      }
    }, true);
  }

  async function init() {
    interceptCartOperations();
    interceptFormSubmissions();
    await reconcileSharedCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.RedmondCartSync = {
    reconcileSharedCart,
    broadcastToken,
    fetchLocalCart,
    getCurrentShopifyCartId: async function () {
      const cart = await fetchLocalCart();
      return cart && cart.token ? cart.token : '';
    },
    syncCartCookie: function (token) {
      broadcastToken(token);
    },
    getCookie,
    getCanonicalProductUrl,
    isHydrogenCartId,
    clearLegacySharedCookie,
    CONFIG
  };
})();
